import React from 'react';
import * as m from 'motion/react-m';

import { spring } from '../../theme/motion';
import { usePrefersReduced } from './useMotionPreference';

/** Vertical travel for the slide-up reveal (px). */
const REVEAL_Y = 16;

/**
 * Shared item variants so a `Reveal` can be driven either on its own
 * (`whileInView`) or as a child of a `StaggerGroup` (which orchestrates the
 * `hidden`→`visible` states). Exported for `StaggerGroup` children to reuse.
 */
export const revealItemVariants = {
  hidden: { opacity: 0, y: REVEAL_Y },
  visible: { opacity: 1, y: 0 },
};

export const revealItemVariantsReduced = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
};

export interface RevealProps {
  children?: React.ReactNode;
  /** Optional className passed through to the motion element. */
  className?: string;
}

/**
 * Fades + slides a block of content up the first time it scrolls into view
 * (`whileInView` with `viewport={{ once: true }}`).
 *
 * Reduced-motion branch: when `usePrefersReduced()` is true, the slide (`y`)
 * is dropped — content fades in (opacity only) with no movement.
 */
export const Reveal: React.FC<RevealProps> = ({ children, ...rest }) => {
  const prefersReduced = usePrefersReduced();
  const v = prefersReduced ? revealItemVariantsReduced : revealItemVariants;

  return (
    <m.div
      initial={v.hidden}
      whileInView={v.visible}
      viewport={{ once: true, amount: 0.2 }}
      transition={spring.gentle}
      {...rest}
    >
      {children}
    </m.div>
  );
};

export default Reveal;
